import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Clock, ArrowLeft } from "lucide-react";
import { fetchPosts } from "@/lib/api";
import { Stagger, StaggerItem } from "@/components/Reveal";
import { toFa } from "@/lib/fa";

export default function Blog() {
  const { data, isLoading } = useQuery({ queryKey: ["posts"], queryFn: fetchPosts });
  const posts = data?.data ?? [];
  const cats = ["همه", ...new Set(posts.map((p) => p.category))];
  const [cat, setCat] = useState("همه");
  const filtered = cat === "همه" ? posts : posts.filter((p) => p.category === cat);

  return (
    <section className="mx-auto max-w-7xl px-5 pb-24 pt-36 lg:px-8" data-testid="blog-page">
      <div className="flex flex-col items-center gap-4 pb-12 text-center">
        <span className="flex items-center gap-3 text-xs font-bold tracking-[0.25em] text-gold">
          <span className="h-px w-8 bg-gold/60" />وبلاگ و اخبار مهاجرت<span className="h-px w-8 bg-gold/60" />
        </span>
        <h1 className="text-3xl font-black sm:text-4xl">تحلیل‌های <span className="gold-text">حقوقی</span> تیم گروه ۷</h1>
        <p className="max-w-xl text-sm leading-8 text-slate-400">آخرین تغییرات قوانین ویزا، راهنماهای گام‌به‌گام و تجربه‌های واقعی پرونده‌ها را اینجا بخوانید.</p>
      </div>

      <div className="mb-10 flex flex-wrap justify-center gap-2" data-testid="blog-filters">
        {cats.map((c) => (
          <button key={c} onClick={() => setCat(c)} className={`rounded-full px-5 py-2 font-display text-xs font-bold transition-all duration-300 ${cat === c ? "bg-gold text-navy-950" : "border border-gold/25 text-slate-300 hover:border-gold/60"}`} data-testid={`blog-filter-${c}`}>
            {c}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">{[0, 1, 2].map((i) => <div key={i} className="h-80 animate-pulse rounded-3xl bg-navy-850/50" />)}</div>
      ) : (
        <Stagger className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {filtered.map((p) => (
            <StaggerItem key={p.slug}>
                <Link to={`/blog/${p.slug}`} className="card-glow group flex h-full flex-col overflow-hidden rounded-3xl border border-gold/15 bg-navy-850" data-testid={`blog-card-${p.slug}`}>
                  <div className="relative h-52 overflow-hidden">
                    <img src={p.image} alt={p.title} className="img-treatment h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    <span className="absolute right-4 top-4 rounded-full bg-navy-950/85 px-3 py-1 text-[11px] font-bold text-gold backdrop-blur">{p.category}</span>
                  </div>
                  <div className="flex flex-1 flex-col p-6">
                    <h3 className="font-display text-base font-bold leading-7 text-slate-50">{p.title}</h3>
                    <p className="mt-3 line-clamp-2 flex-1 text-sm leading-7 text-slate-400">{p.excerpt}</p>
                    <div className="mt-4 flex items-center justify-between border-t border-gold/10 pt-4 text-xs text-slate-500">
                      <span>{p.date}</span>
                      <span className="flex items-center gap-1.5"><Clock size={12} className="text-gold/70" />{toFa(p.readMinutes)} دقیقه مطالعه</span>
                    </div>
                  </div>
                </Link>
              </StaggerItem>
            ))}
        </Stagger>
      )}

      {!isLoading && filtered.length === 0 && (
        <p className="py-16 text-center text-slate-500" data-testid="blog-empty-state">مقاله‌ای در این دسته یافت نشد.</p>
      )}

      <div className="mt-16 text-center">
        <Link to="/contact" className="btn-gold" data-testid="blog-consult-cta">سوال دارید؟ مشاوره رایگان بگیرید<ArrowLeft size={16} /></Link>
      </div>
    </section>
  );
}
